'use client';

import { Heart, PackageCheck } from 'lucide-react';
import type { FairtradePowerRow } from '@/lib/fairtrade-catalog';
import { usePowerCollection } from '@/contexts/PowerCollectionContext';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

type FairtradeCollectionActionsProps = {
  row: FairtradePowerRow;
  isDark?: boolean;
};

export default function FairtradeCollectionActions({
  row,
  isDark = false,
}: FairtradeCollectionActionsProps) {
  const {
    ready,
    isWished,
    isOwned,
    toggleWishFromRow,
    markOwnedFromRow,
  } = usePowerCollection();

  const wished = isWished(row.id);
  const owned = isOwned(row.id);

  const btnBase = cn(
    'inline-flex h-8 w-8 items-center justify-center rounded-lg border transition-all duration-200',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/40',
    'disabled:pointer-events-none disabled:opacity-40',
    isDark
      ? 'border-blue-900/60 bg-[#01012e]/80 text-sky-200/70 hover:border-sky-400/35 hover:text-sky-100'
      : 'border-sky-200 bg-sky-50 text-sky-700 hover:border-sky-300 hover:bg-sky-100'
  );

  return (
    <TooltipProvider delayDuration={250}>
      <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              disabled={!ready || owned}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                void toggleWishFromRow(row);
              }}
              className={cn(
                btnBase,
                wished &&
                  (isDark
                    ? 'border-rose-400/40 bg-rose-500/20 text-rose-200'
                    : 'border-rose-300 bg-rose-50 text-rose-600')
              )}
              aria-label={wished ? 'Nos desejos' : 'Adicionar aos desejos'}
              aria-pressed={wished}
            >
              <Heart
                className={cn(
                  'h-3.5 w-3.5 transition-transform duration-300',
                  wished && 'scale-110 fill-current'
                )}
                aria-hidden
              />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="border-sky-500/30 bg-[#030023] font-poppins text-xs text-sky-50"
          >
            {owned
              ? 'Já conquistado'
              : wished
                ? '♥ Nos desejos'
                : '♡ Adicionar aos desejos'}
          </TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              disabled={!ready || owned}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (owned) return;
                void markOwnedFromRow(row);
              }}
              className={cn(
                btnBase,
                owned &&
                  (isDark
                    ? 'border-emerald-400/40 bg-emerald-500/20 text-emerald-200'
                    : 'border-emerald-300 bg-emerald-50 text-emerald-700')
              )}
              aria-label={owned ? 'Na coleção' : 'Tenho este power'}
              aria-pressed={owned}
            >
              <PackageCheck className="h-3.5 w-3.5" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent
            side="top"
            className="border-sky-500/30 bg-[#030023] font-poppins text-xs text-sky-50"
          >
            {owned ? 'Na coleção' : 'Tenho este power'}
          </TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  );
}
